// src/oura/ask/agreed.ts
//
// Ask Oura (PREVIEW): "approval starts the next job". The client's yes in an
// Answer Plan is read from the doc's own threads, the same read the Client
// tab's document drawer makes (useDocument). An answer counts as agreed when
//   - the question is still ours to act on (state "needs_us", not resolved)
//     and the client's last post opens with AGREED, Yes or Approved, or
//   - the question was resolved WITH the client's agreement (resolvedAgreed).
// It is "not yet work" when no draft a person started in Delivery came from
// it (StartedDraft.fromDocId + fromQuestionId). A resolve without agreement
// (the amber flag) never counts.
//
// The fixture for Nyonya Table already holds 2 such answers (threads thr_2
// and thr_6 of the Answer Plan), so no preview-only data is added here.

import type { DeliveryPayload } from "@service/aeo/delivery";
import type { BusinessDocuments, DocThread, DocumentDetail } from "@service/aeo/clientDocs";

export interface AgreedAnswer {
    threadId: string;
    question: string;
    answer: string;
    by: string;
    at: string;
}

export interface AgreedSet {
    docId: string;
    docTitle: string;
    /** The client's first name as they signed the answers. */
    clientName: string;
    answers: AgreedAnswer[];
}

const YES = /^\s*(agreed|yes|approved)\b/i;

/** The Answer Plan whose threads are read: the newest one with answers back. */
export function answerPlanDocId(board: BusinessDocuments | null): string | null {
    if (!board) return null;
    const plans = board.documents
        .filter((d) => d.docType === "Answer Plan" && d.fileType === "gdoc" && (d.questions?.back ?? 0) > 0)
        .sort((a, b) => (b.modifiedAt ?? "").localeCompare(a.modifiedAt ?? ""));
    return plans[0]?.id ?? null;
}

function draftedFrom(delivery: DeliveryPayload | null, docId: string): Set<string> {
    const ids = new Set<string>();
    for (const d of delivery?.deliverables ?? []) {
        for (const it of d.items) {
            for (const dr of it.drafts ?? []) {
                if (dr.fromDocId === docId && dr.fromQuestionId) ids.add(dr.fromQuestionId);
            }
        }
    }
    return ids;
}

function agreedPost(t: DocThread): { text: string; by: string; at: string } | null {
    if (t.kind !== "question") return null;
    const last = t.posts[t.posts.length - 1];
    if (t.resolved) {
        if (t.resolvedAgreed !== true) return null;
        const client = [...t.posts].reverse().find((p) => p.author.side === "client");
        return client ? { text: client.text, by: client.author.name, at: client.at } : null;
    }
    if (t.state !== "needs_us" || !last || last.author.side !== "client" || !YES.test(last.text)) return null;
    return { text: last.text, by: last.author.name, at: last.at };
}

export function agreedNotYetWork(doc: DocumentDetail | null, delivery: DeliveryPayload | null): AgreedSet | null {
    if (!doc) return null;
    const drafted = draftedFrom(delivery, doc.id);
    const answers: AgreedAnswer[] = [];
    for (const t of doc.threads) {
        if (t.questionId && drafted.has(t.questionId)) continue;
        const yes = agreedPost(t);
        if (yes) answers.push({ threadId: t.id, question: t.text, answer: yes.text, by: yes.by, at: yes.at });
    }
    if (answers.length === 0) return null;
    return {
        docId: doc.id,
        docTitle: doc.docType ?? "Answer Plan",
        clientName: answers[0].by,
        answers,
    };
}

/** "Oura · 2 answers Siew Ling approved in the Answer Plan can become page drafts". */
export function agreedLine(a: AgreedSet): string {
    const n = a.answers.length;
    return `${n} ${n === 1 ? "answer" : "answers"} ${a.clientName} approved in the ${a.docTitle} can become page drafts`;
}

export const agreedKey = (businessId: string, a: AgreedSet): string => `agreed:${businessId}:${a.docId}:${a.answers.map((x) => x.threadId).join(",")}`;
